/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

interface KalendarSecondaryProps {
    /**
     * A string representing the CSS class to be applied to the KalendarSecondaryIcon element to override its default styling.
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     */
    color1?: string;
    /**
     * A number or string representing the height of the KalendarSecondaryIcon element.
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     */
    testId?: string;
    /**
     * A number or string representing the height of the KalendarSecondaryIcon element.
     */
    width?: number | string;
}

/**
 * The `KalendarSecondary` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `KalendarSecondaryProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the KalendarSecondaryIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the KalendarSecondaryIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the KalendarSecondaryIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const KalendarSecondaryComponent = <KalendarSecondary className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const KalendarSecondary = ({
    className = '',
    color1 = '#0050FF',
    height = 20,
    ref,
    testId = 'KalendarSecondary',
    width = 20
}: KalendarSecondaryProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 20 20"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <g clipRule="evenodd" fill={color1} fillRule="evenodd">
            <path d="M17.86 2.14h-3.22V.71a.71.71 0 0 0-1.43 0v1.43H6.8V.71a.72.72 0 0 0-1.43 0v1.43H2.14C.96 2.14 0 3.11 0 4.3V20h20V4.29c0-1.18-.96-2.15-2.14-2.15M2.14 3.57h3.22V5c0 .4.32.71.71.71.4 0 .72-.32.72-.71V3.57h6.42V5a.72.72 0 0 0 1.43 0V3.57h3.22c.39 0 .71.32.71.72v2.5H1.43v-2.5c0-.4.32-.72.71-.72m-.71 4.64v10.36h17.14V8.21z" />
            <path d="M7 9.25h.64c1.32 0 2.4 1.1 2.4 2.43a2.4 2.4 0 0 1-.68 1.71q.66.73.68 1.72a2.4 2.4 0 0 1-2.4 2.43H7c-.43 0-1.29-.18-1.75-.54a.74.74 0 1 1 .93-1.14c.1.07.57.21.82.21h.64c.54 0 .93-.43.93-.96a.95.95 0 0 0-.93-.97h-.96a.73.73 0 0 1-.75-.75c0-.43.36-.75.75-.75h.96c.54 0 .93-.43.93-.96a.95.95 0 0 0-.93-.97H7c-.25 0-.71.15-.82.22-.32.21-.79.18-1.04-.14a.7.7 0 0 1 .11-1A3.4 3.4 0 0 1 7 9.25m5.86.14c.21-.14.5-.14.71 0 .22.11.36.36.36.61v6.79c0 .39-.32.71-.71.71a.7.7 0 0 1-.72-.71V11.2l-.57.33a.67.67 0 0 1-.96-.25.67.67 0 0 1 .25-.97z" />
        </g>
    </svg>
);

KalendarSecondary.displayName = 'KalendarSecondary';